import SpeakerAvatar from '@src/components/common/SpeakerAvatar';
import { getSpeakerColorFromLabel } from '@src/utility/speakerUtils';
import styled from 'styled-components';

import type { DatoSpeaker } from '@src/types.ts/story';

/** Shared react-tooltip id — RecentContent renders the single Tooltip instance. */
export const SPEAKER_FACE_PILE_TOOLTIP_ID = 'home-speaker-face-pile-tooltip';

/**
 * How wide the pile ever gets, counting the "+N" as one of them: four speakers
 * show two faces and a "+2", not three faces and a "+1". The overflow badge is
 * a circle like the rest, so budgeting the faces alone let the pile grow a
 * column wider than the space it was designed into.
 */
const MAX_CIRCLES = 3;

interface SpeakerFacePileProps {
  speakers: DatoSpeaker[];
  /**
   * Avatar diameter in px. The default suits table rows; denser surfaces
   * (the Reel Editor's Section rows) pass a smaller one so speakers read as
   * secondary to the structure around them.
   */
  size?: number;
  /**
   * Which tooltip instance carries the names. Defaults to the shared one; a
   * surface above the clip-preview stack passes its own, since the shared
   * instance's z sits below that stack's backdrop.
   */
  tooltipId?: string;
  /**
   * The gap drawn between overlapping avatars. Defaults to the page background,
   * which separates them on a panel. Over a **photograph** that reads as a black
   * outline around each face rather than as a gap, so a surface on a video frame
   * passes `transparent` and lets the frame show through.
   */
  ringColor?: string;
}

/**
 * Overlapping speaker headshots (face pile) with a "+N" badge for overflow.
 * Hovering an avatar shows the speaker name via the shared tooltip.
 */
export function SpeakerFacePile({
  speakers,
  size = 24,
  tooltipId = SPEAKER_FACE_PILE_TOOLTIP_ID,
  ringColor = 'var(--background)',
}: SpeakerFacePileProps) {
  if (speakers.length === 0) {
    return <NoSpeakers>—</NoSpeakers>;
  }

  // One fewer face once there is an overflow badge to fit beside them.
  const visibleCount =
    speakers.length > MAX_CIRCLES ? MAX_CIRCLES - 1 : MAX_CIRCLES;
  const visibleSpeakers = speakers.slice(0, visibleCount);
  const overflowSpeakers = speakers.slice(visibleCount);
  // Initials and overlap scale with the diameter (9px / -8px at the 24px default).
  const initialsFontSize = `${Math.round(size * 0.375)}px`;

  return (
    <PileWrapper>
      {visibleSpeakers.map(speaker => (
        <AvatarRing
          key={speaker.id}
          $size={size}
          $ringColor={ringColor}
          data-tooltip-id={tooltipId}
          data-tooltip-content={speaker.name}
        >
          <SpeakerAvatar
            name={speaker.name}
            color={getSpeakerColorFromLabel(speaker.id)}
            headshotUrl={speaker.headshots?.[0]?.url}
            size={size}
            initialsLength={2}
            initialsFontSize={initialsFontSize}
          />
        </AvatarRing>
      ))}
      {overflowSpeakers.length > 0 && (
        <AvatarRing
          $size={size}
          $ringColor={ringColor}
          data-tooltip-id={tooltipId}
          data-tooltip-content={overflowSpeakers
            .map(speaker => speaker.name)
            .join(', ')}
        >
          <OverflowBadge $size={size}>+{overflowSpeakers.length}</OverflowBadge>
        </AvatarRing>
      )}
    </PileWrapper>
  );
}

const PileWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const AvatarRing = styled.div<{ $size: number; $ringColor: string }>`
  border-radius: 50%;
  border: 2px solid ${props => props.$ringColor};
  flex-shrink: 0;

  &:not(:first-child) {
    margin-left: ${props => -Math.round(props.$size / 3)}px;
  }

  /* Lift the hovered avatar above its overlapping neighbors. */
  &:hover {
    position: relative;
    z-index: 1;
  }
`;

const OverflowBadge = styled.div<{ $size: number }>`
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--surface-elevated);
  color: var(--text-muted);
  font-size: ${props => Math.round(props.$size * 0.375)}px;
  font-weight: var(--font-weight-semi-bold);
  line-height: 1;
  user-select: none;
`;

const NoSpeakers = styled.span`
  color: var(--border);
  font-size: var(--text-xs);
`;
